import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { CreditCard, CheckCircle2, AlertCircle, ArrowLeft, MessageSquare, Star } from 'lucide-react'
import { db, app } from '../firebase/config'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { getAuth, signInAnonymously } from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions'
import toast from 'react-hot-toast'

const PAYSTACK_PUBLIC_KEY = 'pk_live_d0dac42be7424384d1b9c6dc660d3993efaf5cd6'

const BalancePayment = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  // Get booking ID from URL (amount is taken from booking balance only, not URL)
  const bookingId = searchParams.get('bookingId')
  const phone = searchParams.get('phone')
  
  const [bookingData, setBookingData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [processingPayment, setProcessingPayment] = useState(false)
  const [showCompleteButton, setShowCompleteButton] = useState(false)
  const [paymentReference, setPaymentReference] = useState('')
  const [paymentCompleted, setPaymentCompleted] = useState(false)
  
  // Fetch booking data
  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId) {
        toast.error('Booking ID is required')
        setLoading(false)
        return
      }
      
      try {
        const bookingRef = doc(db, 'bookings', bookingId)
        const bookingSnap = await getDoc(bookingRef)
        
        if (!bookingSnap.exists()) {
          toast.error('Booking not found')
          setLoading(false)
          return
        }
        
        const data = bookingSnap.data()
        setBookingData({
          id: bookingSnap.id,
          ...data
        })
        
        // Check if payment has already been completed
        if (data.balancePaid || data.paymentCompleted || (data.balance !== undefined && Number(data.balance) <= 0)) {
          // Payment already completed, show success view
          setPaymentCompleted(true)
        }
        
        setLoading(false)
      } catch (error) {
        console.error('Error fetching booking:', error)
        toast.error('Failed to load booking information')
        setLoading(false)
      }
    }
    
    fetchBooking()
  }, [bookingId])
  
  // Use only booking balance for charge so URL amount cannot be tampered
  const balanceAmount = bookingData != null && bookingData.balance != null
    ? Number(bookingData.balance)
    : 0
  
  // Check if payment already completed
  const isAlreadyPaid = bookingData?.balancePaid || bookingData?.paymentCompleted || (bookingData?.balance !== undefined && Number(bookingData.balance) <= 0)
  
  // Handle payment via Paystack inline (client-side)
  const handlePayment = async () => {
    // Check if already paid
    if (isAlreadyPaid) {
      toast.error('This booking has already been paid. Please check your payment history.')
      return
    }
    
    if (balanceAmount <= 0) {
      toast.error('Invalid payment amount')
      return
    }
    
    setProcessingPayment(true)
    
    // Build a safe email from phone (Paystack requires an email)
    const rawPhone = (phone || bookingData?.phone || '').toString()
    const digits = rawPhone.replace(/\D/g, '')
    const customerEmail = (digits || 'customer') + '@temp.sparadiseluxe.com'
    
    const startPayment = () => {
      if (!window.PaystackPop) {
        toast.error('Payment system not available. Please check your connection and try again.')
        setProcessingPayment(false)
        return
      }
      
      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email: customerEmail,
        amount: Math.round(balanceAmount * 100), // GHS → pesewas
        currency: 'GHS',
        ref: `BAL_${Date.now()}`,
        callback: function (response) {
          // Payment successful: verify and update booking
          setProcessingPayment(false)
          const ref = response.reference
          setPaymentReference(ref)
          // Reuse existing verification + completion flow
          handleCompletePayment(ref)
        },
        onClose: function () {
          setProcessingPayment(false)
          toast.error('Payment was cancelled. Please try again.')
        },
      })
      
      handler.openIframe()
    }
    
    try {
      // If script already loaded, start immediately
      if (window.PaystackPop) {
        startPayment()
        return
      }
      
      // Otherwise, load Paystack inline script (v1) then start
      const script = document.createElement('script')
      script.src = 'https://js.paystack.co/v1/inline.js'
      script.onload = startPayment
      script.onerror = () => {
        toast.error('Failed to load payment gateway. Please try again.')
        setProcessingPayment(false)
      }
      document.body.appendChild(script)
    } catch (error) {
      console.error('Error starting payment:', error)
      toast.error('An error occurred. Please try again.')
      setProcessingPayment(false)
    }
  }
  
  // Verify and complete payment
  const handleCompletePayment = async (referenceOverride = null) => {
    const referenceToUse = referenceOverride || paymentReference
    if (!referenceToUse) {
      toast.error('Payment reference not found. Please try again.')
      return
    }
    
    setProcessingPayment(true)
    setShowCompleteButton(false)
    
    try {      
      const functionsInstance = getFunctions(app)
      
      const verificationToast = toast.loading('Verifying payment status...', {
        duration: 5000,
      })
      
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Verify payment
      const verifyPayment = httpsCallable(functionsInstance, 'verifyBookingDepositPayment')
      const verifyResult = await verifyPayment({
        reference: referenceToUse
      })
      
      const verifyData = verifyResult.data
      
      toast.dismiss(verificationToast)
      
      if (!verifyData || !verifyData.success) {
        const paymentStatus = verifyData?.status || 'unknown'
        if (paymentStatus === 'abandoned' || paymentStatus === 'cancelled') {
          toast.error('Payment was cancelled. Please try again.')
          setPaymentReference('')
        } else {
          toast.error('Payment verification failed. Please try again.')
          setShowCompleteButton(true)
        }
        setProcessingPayment(false)
        return
      }
      
      // Authenticate anonymously before updating booking
      const auth = getAuth(app)
      let user = auth.currentUser
      if (!user) {
        try {
          const userCredential = await signInAnonymously(auth)
          user = userCredential.user
        } catch (authError) {
          console.error('Error authenticating:', authError)
          toast.error('Failed to authenticate. Please try again.')
          setProcessingPayment(false)
          setShowCompleteButton(true)
          return
        }
      }

      // Update booking with balance payment
      if (bookingId) {
        const bookingRef = doc(db, 'bookings', bookingId)
        await updateDoc(bookingRef, {
          balancePaid: true,
          balanceTransactionRef: verifyData.reference,
          paymentCompleted: true,
          updatedAt: new Date().toISOString()
        })
      }
      
      // Send thank you SMS with review link
      try {
        // Extract and clean phone number - find first valid phone number
        let rawPhone = phone || bookingData?.phone || ''
        
        // Extract first valid phone number pattern
        // Match: 0 followed by 9 digits, or 233 followed by 9 digits, or +233 followed by 9 digits
        let customerPhone = ''
        
        // Try to find a valid Ghana phone number pattern
        // Pattern 1: 0XXXXXXXXX (10 digits starting with 0)
        const pattern1 = rawPhone.match(/0\d{9}/)
        if (pattern1) {
          customerPhone = '233' + pattern1[0].substring(1)
        } else {
          // Pattern 2: 233XXXXXXXXX (12 digits starting with 233)
          const pattern2 = rawPhone.match(/233\d{9}/)
          if (pattern2) {
            customerPhone = pattern2[0]
          } else {
            // Pattern 3: +233XXXXXXXXX
            const pattern3 = rawPhone.match(/\+233\d{9}/)
            if (pattern3) {
              customerPhone = pattern3[0].substring(1)
            } else {
              // Fallback: extract first sequence of 9-12 digits
              const digits = rawPhone.match(/\d{9,12}/)
              if (digits) {
                customerPhone = digits[0]
                // Format with country code if needed
                if (customerPhone.startsWith('0')) {
                  customerPhone = '233' + customerPhone.substring(1)
                } else if (!customerPhone.startsWith('233')) {
                  customerPhone = '233' + customerPhone
                }
              }
            }
          }
        }
        
        // Validate phone number (should be 12 digits: 233 + 9 digits)
        if (customerPhone.length !== 12 || !customerPhone.startsWith('233')) {
          customerPhone = ''
        }
        
        const customerName = bookingData?.name || ''
        
        if (customerPhone && customerPhone.length === 12 && customerName) {
          const sendPaymentCompletion = httpsCallable(functionsInstance, 'sendPaymentCompletionSMS')
          
          await sendPaymentCompletion({
            name: customerName,
            phone: customerPhone,
            bookingId
          })
        }
      } catch (smsError) {
        // Don't fail the payment if SMS fails
      }
      
      toast.success('Payment completed successfully!')
      setProcessingPayment(false)
      setShowCompleteButton(false)
      setPaymentReference('')
      setPaymentCompleted(true)
    } catch (error) {
      console.error('Error verifying payment:', error)
      setProcessingPayment(false)
      setShowCompleteButton(true)
      toast.error('Failed to verify payment. Please try again or contact support.')
    }
  }
  
  if (loading) {
  return (
      <div className="spa-section min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading payment information...</p>
        </div>
      </div>
    )
  }
  
  if (!bookingData) {
    return (
      <div className="spa-section min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Booking Not Found</h2>
          <p className="text-gray-600 mb-6">Unable to load booking information. Please check your payment link.</p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Go Home</span>
          </button>
        </div>
      </div>
    )
  }
  
  // If already paid, show success message directly
  if (isAlreadyPaid && bookingData) {
    return (
      <div className="spa-section min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
        <div className="container-spa max-w-2xl mx-auto py-12">
          <div className="card bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-8 md:p-10">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-6 shadow-lg">
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-800 mb-2">
                Payment Already Completed
              </h1>
              <p className="text-gray-600">
                This booking has already been paid successfully.
              </p>
            </div>
            
            {/* Booking Info */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 mb-6 border border-purple-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Booking Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-semibold text-gray-800">{bookingData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Service:</span>
                  <span className="font-semibold text-gray-800">{bookingData.category} - {bookingData.serviceType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-semibold text-gray-800">
                    {bookingData.date ? new Date(bookingData.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time:</span>
                  <span className="font-semibold text-gray-800">{bookingData.time || 'N/A'}</span>
                </div>
                <div className="flex justify-between mt-3 pt-3 border-t border-purple-200">
                  <span className="text-gray-600 font-semibold">Payment Status:</span>
                  <span className="font-bold text-purple-600">Paid</span>
                </div>
              </div>
            </div>
            
            {/* Share Experience */}
            <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Thank You!</h3>
                <p className="text-gray-600 mb-6">
                  We hope you enjoyed your service!
                </p>
                <Link
                  to="/?review=true"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <MessageSquare className="w-5 h-5" />
                  <span>Share Your Experience</span>
                  <Star className="w-5 h-5" />
                </Link>
                <div className="mt-4">
                  <button
                    onClick={() => navigate('/')}
                    className="text-gray-600 hover:text-gray-800 text-sm underline"
                  >
                    Go to Home
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="spa-section min-h-screen bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
      <div className="container-spa max-w-2xl mx-auto py-12">
        <div className="card bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-6 shadow-lg">
              <CreditCard className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-800 mb-2">
              Complete Balance Payment
            </h1>
            <p className="text-gray-600">
              Pay the remaining balance for your Service booked
            </p>
          </div>
          
          {/* Booking Info */}
            {bookingData && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 mb-6 border border-purple-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Booking Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-semibold text-gray-800">{bookingData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Service:</span>
                  <span className="font-semibold text-gray-800">{bookingData.category} - {bookingData.serviceType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-semibold text-gray-800">
                    {bookingData.date ? new Date(bookingData.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time:</span>
                  <span className="font-semibold text-gray-800">{bookingData.time || 'N/A'}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Show payment form only if payment not completed */}
          {!paymentCompleted ? (
            <>
              {/* Payment Amount */}
              <div className="bg-white rounded-xl p-6 mb-6 border-2 border-purple-100">
                <div className="text-center">
                  <p className="text-gray-600 mb-2">Balance to Pay</p>
                  <p className="text-4xl font-bold text-purple-600 mb-4">GHS {balanceAmount.toFixed(2)}</p>
                  {bookingData && bookingData.deposit > 0 && (
                    <div className="text-sm text-gray-500">
                      <p>Total: GHS {bookingData.price?.toFixed(2) || '0.00'}</p>
                      <p>Deposit Paid: GHS {bookingData.deposit?.toFixed(2) || '0.00'}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Payment Button */}
              <div className="space-y-4">
                <button
                  onClick={handlePayment}
                  disabled={processingPayment || balanceAmount <= 0 || isAlreadyPaid}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                >
                  {processingPayment ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      <span>Pay Balance</span>
                    </>
                  )}
                </button>
              </div>
              
              {/* Info Notice */}
              <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-800">
                  <strong>Note:</strong> After clicking \"Pay Balance\", you'll be redirected to PayStack to complete your payment securely.
                </p>
              </div>
            </>
          ) : (
            /* Payment Success - Review Link */
            <div className="mt-6 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
              <div className="text-center">
                <CheckCircle2 className="w-16 h-16 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Payment Successful!</h3>
                <p className="text-gray-600 mb-6">
                  Thank you for your payment. We hope you enjoyed your service!
                </p>
                <Link
                  to="/?review=true"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  <MessageSquare className="w-5 h-5" />
                  <span>Share Your Experience</span>
                  <Star className="w-5 h-5" />
                </Link>
                <div className="mt-4">
                  <button
                    onClick={() => navigate('/')}
                    className="text-gray-600 hover:text-gray-800 text-sm underline"
                  >
                    Go to Home
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BalancePayment

